import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import { AI_MODELS, inputCls } from "./AFields";

const OPENAI_MODELS = AI_MODELS.filter(([value]) => value.startsWith("gpt"));

export default function AICreate({ entity, title, open, onClose, onApply }) {
  const [prompt, setPrompt] = useState("");
  const [language, setLanguage] = useState("bilingual");
  const [model, setModel] = useState("gpt-5.4-mini");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) setResult(null);
  }, [open]);

  if (!open) return null;

  const generate = async () => {
    if (!prompt.trim()) {
      toast.error("Tell AI what you want to create or change");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/ai/cms/generate", { entity, prompt, language, model });
      setResult(r.data.fields);
      toast.success("AI draft content generated — review before saving");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!result) return;
    try {
      await api.post("/ai/cms/accept", { entity, title: result.title || result.title_en || result.heading || "" });
    } catch {}
    onApply(result);
    onClose();
  };

  const displayTitle = result?.title || result?.title_en || result?.heading || "Untitled AI draft";

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-navy/60 p-4 backdrop-blur-sm" data-testid="ai-create-modal">
      <div className="my-8 w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-bold text-navy"><Sparkles size={18} className="mr-2 inline text-saffron-600" />Create with AI — {title}</h2>
            <p className="mt-1 text-xs text-slate-500">AI prepares editable draft fields only. Nothing is saved or published here.</p>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-gray-100" data-testid="ai-create-close" aria-label="Close"><X size={18} /></button>
        </div>

        <label className="mt-6 block text-xs font-bold uppercase tracking-wider text-slate-500">Tell AI what you want to create or change</label>
        <textarea rows={6} value={prompt} onChange={(e) => setPrompt(e.target.value)}
          placeholder="Write an article in English and Hindi about inclusive education for children with disabilities in Uttar Pradesh, approximately 800 words."
          className={`${inputCls} mt-2`} data-testid="ai-create-prompt" />
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <select value={language} onChange={(e) => setLanguage(e.target.value)} className={`${inputCls} !w-44`} data-testid="ai-create-language">
            <option value="english">English</option>
            <option value="hindi">Hindi</option>
            <option value="bilingual">Bilingual — English + Hindi</option>
          </select>
          <select value={model} onChange={(e) => setModel(e.target.value)} className={`${inputCls} !w-56`} data-testid="ai-create-model">
            {OPENAI_MODELS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <button onClick={generate} disabled={busy} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="ai-create-generate">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {result ? "Regenerate" : "Generate"}
          </button>
        </div>
        {busy && <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-saffron-600" data-testid="ai-create-loading"><Loader2 size={15} className="animate-spin" /> Generating…</p>}

        {result && (
          <div className="mt-6 rounded-lg border border-brandgreen/25 bg-brandgreen-50 p-4" data-testid="ai-create-result">
            <p className="text-xs font-bold uppercase tracking-wider text-brandgreen">Generated draft preview</p>
            <h3 className="mt-2 font-display text-lg font-bold text-navy">{displayTitle}</h3>
            <p className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md bg-white p-3 text-xs leading-relaxed text-slate-600">
              {JSON.stringify(result, null, 2)}
            </p>
            <p className="mt-2 text-xs text-slate-500">Open this in the normal editor to review, edit, preview, save as draft, and publish.</p>
          </div>
        )}

        <div className="mt-7 flex flex-wrap justify-end gap-3">
          <button onClick={onClose} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy" data-testid="ai-create-cancel">Cancel</button>
          {result && (
            <button onClick={apply} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid="ai-create-open-editor">
              <RefreshCw size={14} /> Open in Editor
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
